import React, { useState, useEffect } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import {
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  LinearProgress,
  Chip,
  Stack,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SweetAlert2 from "react-sweetalert2";
import { useAuth } from "../../auth/auth_provider.jsx";
import { useLocalStorage } from "../../auth/useLocalStorage";
import Compressor from "compressorjs";
import { useLocation, useNavigate } from "react-router-dom";
import WifiIcon from "@mui/icons-material/Wifi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import QrCodeIcon from "@mui/icons-material/QrCode";

function Copyright(props) {
  return (
    <Typography variant="body2" color="darkslategray" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="https://diskominfo.makassarkota.go.id/">
        Diskominfo Kota Makassar
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
    },
    secondary: {
      main: '#8ff9ab',
    },
  },
  typography: {
    h5: {
      fontWeight: 600,
    },
  },
});

const LaporanInternet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [swalProps, setSwalProps] = useState({});
  const [progress, setProgress] = useState(0);
  const [successSend, setSuccess] = useState(null);
  
  // Safe destructuring with fallback
  const data_longwis = location.state?.data_longwis || null;
  const { pic } = useAuth();

  // Redirect if no data_longwis is provided
  useEffect(() => {
    if (!data_longwis) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Data Longwis tidak ditemukan. Silakan pilih lokasi terlebih dahulu.",
        didClose: () => {
          navigate('/pic/internetlongwis');
        }
      });
    }
  }, [data_longwis, navigate]);

  const [data, setData] = useState({
    status_fisik: "",
    serial_number: "",
    foto_ont: null,
    foto_qr: null,
    foto_no_seri: null,
  });

  const uploadFile = (file, key) => {
    new Compressor(file, {
      quality: 0.7,
      maxWidth: 450,
      success(result) {
        setData({ ...data, [key]: result });
      },
      error(err) {
        console.log(err);
      },
    });
  };

  const packFiles = async () => {
    if (!data_longwis) {
      throw new Error('Data Longwis tidak tersedia');
    }
    
    const form_data = new FormData();
    form_data.append("foto_ont", data.foto_ont);
    form_data.append("foto_no_seri", data.foto_no_seri);
    form_data.append("foto_qr", data.foto_qr);
    form_data.append("serial_number", data.serial_number);
    form_data.append("status", data.status_fisik);
    form_data.append("id_user", pic.user.id);
    form_data.append("id_longwis", data_longwis.id);

    return form_data;
  };

  const sendData = async (event) => {
    event.preventDefault();
    
    // Validate required data
    if (!data_longwis) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Data Longwis tidak tersedia. Silakan pilih lokasi terlebih dahulu.",
      });
      return;
    }

    if (!data.foto_ont || !data.foto_no_seri || !data.foto_qr) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Semua foto harus diupload sebelum mengirim data.",
      });
      return;
    }

    try {
      const file_data = await packFiles();

      axios
        .post(process.env.REACT_APP_API_URL + "api/internet", file_data, {
          headers: {
            "Authorization": process.env.REACT_APP_API_AUTH,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
              progressEvent.target.getResponseHeader(
                "x-decompressed-content-length"
              );
            if (totalLength) {
              setProgress(Math.round((progressEvent.loaded * 100) / totalLength));
            }
          },
        })
        .then(async (res) => {
          if (res.status === 200) {
            setSuccess(true);
            await setSwalProps({
              show: true,
              title: "Sukses",
              icon: "success",
              text: "Data telah ditambahkan",
            });
          } else {
            setSwalProps({
              show: true,
              title: "Gagal",
              icon: "error",
              text: res.data.message,
            });
            return;
          }
        })
        .catch((err) => {
          setSwalProps({
            show: true,
            title: "Error",
            icon: "error",
            text: "Terjadi kesalahan saat mengirim data",
          });
        });
    } catch (error) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: error.message || "Terjadi kesalahan saat memproses data",
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1dc39f 0%, #8ff9ab 100%)',
        py: 3
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <SweetAlert2
            {...swalProps}
            didClose={() => {
              setSwalProps({});
            }}
            onConfirm={() => {
              if(successSend){
                navigate("/pic/internetlongwis")
              }
            }}
          />
          <Paper
            elevation={10}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: 3
              }}
            >
              <WifiIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            
            <Typography component="h1" variant="h5" sx={{ mb: 1, color: 'primary.main' }}>
              Pengecekan Internet
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
              {data_longwis 
                ? `Lengkapi data pengecekan internet untuk: ${data_longwis.nama_lokasi || 'Lokasi tidak diketahui'}` 
                : 'Memuat data lokasi...'}
            </Typography>

            {!data_longwis ? (
              <Alert severity="warning" sx={{ width: '100%', mb: 3 }}>
                <Typography>
                  Data lokasi Longwis tidak tersedia. Anda akan diarahkan kembali ke halaman sebelumnya.
                </Typography>
              </Alert>
            ) : (
              <Box component="form" onSubmit={sendData} sx={{ width: '100%' }}>
              {/* Status Internet */}
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <WifiIcon color="primary" />
                    <Typography variant="h6">Status Internet</Typography>
                  </Stack>
                  <FormControl fullWidth>
                    <InputLabel size="small" id="select-status-internet">
                      {"Pilih Status Internet"}
                    </InputLabel>
                    <Select
                      labelId="select-status-internet"
                      id="status-internet"
                      value={data.status_fisik}
                      required
                      label="Pilih Status Internet"
                      size="small"
                      onChange={(e) =>
                        setData({ ...data, status_fisik: e.target.value })
                      }
                    >
                      <MenuItem value={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip label="Online" color="success" size="small" />
                          <span>Internet Online</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip label="Offline" color="error" size="small" />
                          <span>Internet Offline</span>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Upload Photos */}
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <PhotoCameraIcon color="primary" />
                    <Typography variant="h6">Upload Foto</Typography>
                  </Stack>

                  {/* Upload Foto ONT Depan */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <Input
                        accept="image/*"
                        id="upload-ont"
                        type="file"
                        sx={{ display: "none" }}
                        onChange={(e) => uploadFile(e.target.files[0], "foto_ont")}
                      />
                      <label htmlFor="upload-ont">
                        <Button 
                          variant="outlined" 
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ 
                            py: 1.5,
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            '&:hover': {
                              borderStyle: 'dashed',
                              borderWidth: 2,
                            }
                          }}
                        >
                          Upload Foto ONT Depan
                        </Button>
                      </label>
                      {data.foto_ont && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <img
                            src={URL.createObjectURL(data.foto_ont)}
                            alt="ONT Preview"
                            style={{ 
                              width: '100%', 
                              maxWidth: 300,
                              borderRadius: 8,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                            ✓ Foto ONT Depan berhasil diupload
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  </Box>

                  {/* Upload Foto ONT Belakang */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <Input
                        accept="image/*"
                        id="upload-serial"
                        type="file"
                        sx={{ display: "none" }}
                        onChange={(e) =>
                          uploadFile(e.target.files[0], "foto_no_seri")
                        }
                      />
                      <label htmlFor="upload-serial">
                        <Button 
                          variant="outlined" 
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ 
                            py: 1.5,
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            '&:hover': {
                              borderStyle: 'dashed',
                              borderWidth: 2,
                            }
                          }}
                        >
                          Upload ONT Belakang
                        </Button>
                      </label>
                      {data.foto_no_seri && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <img
                            src={URL.createObjectURL(data.foto_no_seri)}
                            alt="Serial Number Preview"
                            style={{ 
                              width: '100%', 
                              maxWidth: 300,
                              borderRadius: 8,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                            ✓ Foto ONT Belakang berhasil diupload
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  </Box>

                  {/* Upload Foto QR */}
                  <Box>
                    <FormControl fullWidth>
                      <Input
                        accept="image/*"
                        id="upload-qr"
                        type="file"
                        sx={{ display: "none" }}
                        onChange={(e) => uploadFile(e.target.files[0], "foto_qr")}
                      />
                      <label htmlFor="upload-qr">
                        <Button 
                          variant="outlined" 
                          component="span"
                          startIcon={<QrCodeIcon />}
                          fullWidth
                          sx={{ 
                            py: 1.5,
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            '&:hover': {
                              borderStyle: 'dashed',
                              borderWidth: 2,
                            }
                          }}
                        >
                          Upload Foto QR Code
                        </Button>
                      </label>
                      {data.foto_qr && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <img
                            src={URL.createObjectURL(data.foto_qr)}
                            alt="QR Preview"
                            style={{ 
                              width: '100%', 
                              maxWidth: 300,
                              borderRadius: 8,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                            ✓ Foto QR Code berhasil diupload
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>

              {/* Serial Number */}
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Nomor Seri</Typography>
                  <TextField
                    type="text"
                    fullWidth
                    label="Nomor Seri Cloud NVR"
                    placeholder="Masukkan nomor seri perangkat"
                    value={data.serial_number}
                    required
                    onChange={(e) =>
                      setData({ ...data, serial_number: e.target.value })
                    }
                  />
                </CardContent>
              </Card>

              {/* Upload Progress */}
              {progress > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Mengirim data... {progress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #1dc39f, #8ff9ab)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #17a085, #7ce89a)',
                  }
                }}
              >
                Kirim Data
              </Button>
            </Box>
            )}
            
            <Copyright sx={{ mt: 4 }} />
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default LaporanInternet;
